<script lang="ts">
import type { ClassValue } from 'tailwind-variants'
import type { CardGeneric } from '~/types/cards'
import type { File } from '~/types/file'
import type { TVComponent } from '~/types/tv'
import { type PrimitiveProps } from '../../atoms/Primitive'
import { GridImageCardsStyles } from './GridImageCards.styles'

type TVGridImageCards = TVComponent<typeof GridImageCardsStyles>

export interface GridImageCardsProps extends Pick<PrimitiveProps, 'as'> {
  appearance?: TVGridImageCards['variants']['appearance']
  class?: ClassValue
  classNames?: TVGridImageCards['slots']
  localIcon?: File
  local?: string
  title?: string
  subTitle?: string
  description?: string
  boxTitle?: string
  boxDescription?: string
  image?: File
  items?: CardGeneric[]
  itemColors?: readonly string[]
  comingSoon?: boolean
}

export interface GridSlots {
  default(): any
  localIcon(props: { class: string }): any
}
</script>

<script setup lang="ts">
const { as = 'span', itemColors, classNames, ...restProps } = defineProps<GridImageCardsProps>()
const slots = defineSlots<GridSlots>()

const { t, locale } = useI18n()

const styles = computed(() => GridImageCardsStyles({ appearance: restProps?.appearance }))

const appearanceItemColorsMap: Record<string, readonly string[]> = {
  default: ['bg-neutral-5', 'bg-neutral-5', 'bg-neutral-5', 'bg-neutral-5'],
  'costa-do-sauipe': ['bg-foundation-verde3', 'bg-foundation-amarelo', 'bg-foundation-azul1', 'bg-foundation-laranja'],
  'rio-quente': ['bg-foundation-verde3', 'bg-foundation-azul2', 'bg-foundation-azul1', 'bg-foundation-laranja']
}

const finalItemColors = computed(() => {
  // Se itemColors foi passado, usa ele
  if (itemColors && itemColors.length > 0) {
    return itemColors
  }
  // Caso contrário, usa as cores da appearance
  return appearanceItemColorsMap[restProps?.appearance ?? 'default'] ?? []
})

const getCardClass = (index: number) => {
  if (!finalItemColors.value || finalItemColors.value.length === 0) {
    return classNames?.cardStyle
  }
  return finalItemColors.value[index % finalItemColors.value.length]
}

const iconSrcStyle = computed(() => {
  if (!restProps.localIcon?.url) {
    return {}
  }
  return {
    '--icon-src': `url('${restProps.localIcon.url}')`
  }
})
</script>

<template>
  <section :class="styles.root({ class: [restProps?.class, classNames?.root] })">
    <div class="container relative w-full px-5 md:px-7 flex flex-col gap-6 md:gap-8">
      <!-- Header -->
      <div class="flex flex-col">
        <div v-if="restProps.local" class="flex items-center justify-start md:justify-center gap-1 mb-1">
          <div
            :style="iconSrcStyle"
            :class="[styles.icon({ class: classNames?.icon }), 'fidelidade-wrapper-icon w-4 h-4 items-center']"
          ></div>
          <span :class="styles.iconLabel({ class: classNames?.iconLabel })">
            {{ local }}
          </span>
        </div>
        <div class="flex flex-col gap-6 md:gap-2">
          <h2 :class="styles.title({ class: classNames?.title })">
            {{ title }}
          </h2>
          <div v-if="image?.url" class="relative w-full md:hidden">
            <div v-if="comingSoon" class="absolute bottom-0 z-1 p-4 grid grid-cols-[20px_1fr]">
              <NuxtImg
                v-if="localIcon"
                :src="localIcon.url"
                :alt="localIcon.alt || ''"
                class="fidelidade-icon-white w-4 h-4 items-center text-neutral-6"
                preset="iconSm"
                loading="lazy"
              />
              <span class="typo-p3 uppercase text-neutral-6">
                {{ local }}
              </span>
              <div class="typo-p1 text-neutral-6 col-span-2">{{ t('loyalty.passports.comingSoon') }}</div>
            </div>
            <!-- Image -->
            <NuxtImg
              :src="image.url"
              :alt="image.alt ?? ''"
              class="block md:hidden relative w-full rounded-3xl object-cover h-[216px]"
              sizes="sm:100vw"
              loading="lazy"
              format="webp"
            />
          </div>
          <p v-if="subTitle" :class="styles.subTitle({ class: classNames?.subTitle })">
            {{ subTitle }}
          </p>
        </div>
      </div>
      <!-- Content: image + cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div v-if="image?.url" class="hidden md:block relative w-full">
          <div v-if="comingSoon" class="absolute bottom-0 z-1 p-4 grid grid-cols-[20px_1fr]">
            <NuxtImg
              v-if="localIcon"
              :src="localIcon.url"
              :alt="localIcon.alt || ''"
              class="fidelidade-icon-white w-4 h-4 items-center text-neutral-6"
              preset="iconSm"
              loading="lazy"
            />
            <span class="typo-p3 uppercase text-neutral-6">
              {{ local }}
            </span>
            <div class="typo-p1 text-neutral-6 col-span-2">{{ t('loyalty.passports.comingSoon') }}</div>
          </div>
          <!-- Image -->
          <NuxtImg
            :src="image.url"
            :alt="image.alt ?? ''"
            class="relative md:block w-full min-h-80 md:h-full rounded-3xl object-cover"
            sizes="md:50vw lg:50vw xl:50vw 2xl:50vw"
            loading="lazy"
            format="webp"
          />
        </div>
        <!-- Cards grid -->
        <div>
          <div v-if="boxTitle || boxDescription" class="grid gap-2 mb-6">
            <h3 :class="styles.boxTitle({ class: classNames?.boxTitle })">
              {{ boxTitle }}
            </h3>
            <p :class="styles.boxDescription({ class: classNames?.boxDescription })">
              {{ boxDescription }}
            </p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 w-full">
            <div
              v-for="(item, index) in items"
              :key="index"
              class="p-4 rounded-3xl flex flex-col gap-2"
              :class="getCardClass(index)"
            >
              <!-- Icon + Title row (mobile) / Icon then Title (desktop) -->
              <div class="flex flex-row md:flex-col items-center md:items-start gap-2 md:gap-1">
                <NuxtImg
                  v-if="item.icon?.url"
                  :src="item.icon.url"
                  :alt="item.icon.alt ?? ''"
                  class="w-6 h-6 object-contain shrink-0"
                  preset="iconLg"
                  loading="lazy"
                />
                <div v-else class="w-6 h-6 shrink-0" />
                <h3 class="typo-h3 md:typo-h2" :class="styles.cardTitle({ class: classNames?.cardTitle })">
                  {{ item.title }}
                </h3>
              </div>

              <!-- Description -->
              <p
                class="pl-8 md:pl-0 typo-p2 md:typo-p1"
                :class="styles.cardSubTitle({ class: classNames?.cardSubTitle })"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer description -->
      <p
        v-if="description"
        class="text-center text-foundation-verde1 text-base md:text-xl font-bold font-mplus uppercase leading-5 md:leading-6"
        :class="styles.description({ class: classNames?.description })"
      >
        {{ description }}
      </p>
    </div>
  </section>
</template>
